import { useState } from "react";
import { ScanLine, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, btnGhost, btnPrimary } from "@/lib/studioApi";
import { uploadPhoto, mediaUrl } from "@/lib/photos";

// Photo de la plaque signalétique -> marque / modèle / puissance proposés (validation par l'artisan)
export const PlateReader = ({ dossierId, onApplied }) => {
  const [busy, setBusy] = useState(false);
  const [mediaId, setMediaId] = useState("");
  const [fields, setFields] = useState(null);
  const [selected, setSelected] = useState({});
  const token = localStorage.getItem("devisto_token");

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setFields(null);
    try {
      const media = await uploadPhoto(file, { token });
      setMediaId(media.id);
      await studio.post(`/dossiers/${dossierId}/photos`, { media_id: media.id, category: "plaque" });
      const { data } = await studio.post(`/dossiers/${dossierId}/plate`, { media_id: media.id, category: "plaque" }, { timeout: 240000 });
      setFields(data.fields);
      setSelected(Object.fromEntries(Object.keys(data.fields).map((k) => [k, true])));
      toast.success(`Plaque lue (fiabilité ${data.confidence}). Vérifiez avant d'appliquer.`);
    } catch (e) {
      toast.error(apiError(e, "Lecture impossible. Rapprochez-vous et évitez les reflets."));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const chosen = Object.fromEntries(Object.entries(fields).filter(([k]) => selected[k]));
    if (!Object.keys(chosen).length) return toast.error("Sélectionnez au moins une information.");
    try {
      await studio.post(`/dossiers/${dossierId}/apply-fields`, { fields: chosen });
      toast.success("Fiche technique complétée.");
      setFields(null);
      onApplied?.();
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible."));
    }
  };

  return (
    <div data-testid="plate-reader" className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
      <p className="text-sm font-semibold text-[#0F2645] inline-flex items-center gap-2">
        <ScanLine className="w-4 h-4 text-[#3A5CC8]" /> Lire la plaque signalétique
      </p>
      <p className="mt-1 text-xs text-[#5C6A87] leading-relaxed">
        Photographiez la plaque : l'IA propose marque, modèle, puissance, année et n° de série. Vous validez avant d'enregistrer.
      </p>

      <label className={`${btnGhost} mt-4 cursor-pointer`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
        {busy ? "Lecture en cours…" : "Photographier la plaque"}
        <input
          data-testid="plate-input"
          type="file"
          accept="image/*,.heic,.heif"
          capture="environment"
          className="hidden"
          disabled={busy}
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
        />
      </label>

      {fields && (
        <div data-testid="plate-result" className="mt-4 rounded-xl border border-[#3A5CC8]/20 bg-[#EEF1FC]/60 p-4">
          <div className="flex gap-4">
            {mediaId && <img src={mediaUrl(mediaId)} alt="Plaque" loading="lazy" className="hidden sm:block w-28 h-28 rounded-lg object-cover border border-[#E2E7F7]" />}
            <div className="flex-1 space-y-1.5">
              {Object.entries(fields).map(([label, value]) => (
                <label key={label} data-testid={`plate-field-${label}`} className="flex items-center gap-2.5 text-sm">
                  <input type="checkbox" checked={!!selected[label]} onChange={(e) => setSelected({ ...selected, [label]: e.target.checked })} className="h-4 w-4 accent-[#3A5CC8]" />
                  <span className="text-[#5C6A87]">{label} :</span>
                  <span className="font-medium text-[#0F2645]">{value}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button type="button" data-testid="plate-apply-btn" onClick={apply} className={btnPrimary}>
              <Check className="w-4 h-4" /> Appliquer à la fiche
            </button>
            <button type="button" data-testid="plate-discard-btn" onClick={() => setFields(null)} className={btnGhost}>
              <X className="w-3.5 h-3.5" /> Ignorer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
